import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, PlayCircle, Search } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../foundation/altzero_base/base/components/ui/dialog';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { siteService } from '../../services/siteService';
import { organisationService } from '../../services/organisationService';
import { ntaService } from '../../services/ntaService';
import type { NtaRun } from '../../lib/nta';
import type { BvOrganisation, BvSite } from '../../types/billValidationTypes';

// Start an NTA run: one site (adhoc) or a filtered portfolio (bulk). The server
// queues every site; the NTA page then drives the run slice by slice.

type RunNtaDialogProps = {
  open: boolean;
  onClose: () => void;
  onStarted: (run: NtaRun) => void;
};

const STATES = ['', 'NSW', 'VIC', 'QLD', 'SA', 'TAS', 'ACT'];
const today = () => new Date().toISOString().slice(0, 10);

export const RunNtaDialog = ({ open, onClose, onStarted }: RunNtaDialogProps) => {
  const [mode, setMode] = useState<'site' | 'portfolio'>('site');
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<BvSite[] | null>(null);
  const [site, setSite] = useState<BvSite | null>(null);
  const [distributor, setDistributor] = useState('');
  const [clientId, setClientId] = useState('');
  const [distributors, setDistributors] = useState<string[]>([]);
  const [clients, setClients] = useState<BvOrganisation[]>([]);
  const [state, setState] = useState('');
  const [runDate, setRunDate] = useState(today());
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pick lists, loaded once the dialog opens.
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    void Promise.all([ntaService.listDistributors(), organisationService.list()]).then(([d, c]) => {
      if (!mounted) return;
      if (d.success) setDistributors(d.data ?? []);
      if (c.success) setClients(c.data ?? []);
      if (!d.success || !c.success) setError(d.error || c.error || 'Could not load pick lists');
    });
    return () => { mounted = false; };
  }, [open]);

  const pickClient = async (id: string) => {
    setClientId(id);
    setSite(null);
    setMatches(null);
    if (!id || mode !== 'site') return;
    setBusy(true);
    const res = await siteService.listByClient(id);
    setBusy(false);
    if (!res.success) { setError(res.error || 'Could not load sites'); return; }
    const sites = (res.data ?? []).sort((a, b) => (a.nmi ?? '').localeCompare(b.nmi ?? ''));
    setMatches(sites);
    setSite(sites.length === 1 ? sites[0] : null);
  };

  const search = async () => {
    if (!query.trim()) return;
    setBusy(true);
    setError(null);
    const res = await siteService.list({ search: query.trim(), size: 10, sort: 'nmi', sortDir: 'asc' });
    setBusy(false);
    if (!res.success) { setError(res.error || 'Site search failed'); return; }
    const sites = res.data?.sites ?? [];
    setMatches(sites);
    setSite(sites.length === 1 ? sites[0] : null);
  };

  const start = async () => {
    setBusy(true);
    setError(null);
    const res = await ntaService.startRun({
      ...(mode === 'site' && site ? { siteIds: [site.id] } : {}),
      ...(mode === 'portfolio' && distributor ? { distributor } : {}),
      ...(mode === 'portfolio' && clientId ? { clientId } : {}),
      ...(mode === 'portfolio' && state ? { state } : {}),
      runDate,
      label: label.trim() || (mode === 'site' ? `Adhoc · ${site?.nmi ?? ''}` : `Portfolio${clientId ? ` · ${clients.find((c) => c.id === clientId)?.name ?? ''}` : ''}${distributor ? ` · ${distributor}` : ''}${state ? ` · ${state}` : ''}`),
    });
    setBusy(false);
    if (!res.success || !res.data) { setError(res.error || 'Could not start the run'); return; }
    onStarted(res.data);
    onClose();
  };

  const canStart = !busy && (mode === 'portfolio' || !!site);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Run Network Tariff Analysis</DialogTitle>
          <DialogDescription>
            Projects the next 12 months on the current tariff and every alternative with an eligibility rule in force.
            Nothing is sent to a customer.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2" role="tablist">
          {(['site', 'portfolio'] as const).map((m) => (
            <button key={m} type="button" role="tab" aria-selected={mode === m}
              onClick={() => { setMode(m); setClientId(''); setMatches(null); setSite(null); }}
              className={cn('rounded-full border px-3 py-1 text-xs',
                mode === m ? 'border-[#3790C5] bg-[#3790C5] text-white' : 'bg-white text-gray-700 hover:bg-gray-50')}>
              {m === 'site' ? 'One site' : 'Portfolio'}
            </button>
          ))}
        </div>

        {mode === 'site' ? (
          <div className="space-y-2">
            <label htmlFor="nta-run-client" className="block text-xs text-gray-600">
              Client
              <select id="nta-run-client" className="mt-1 block w-full rounded border px-2 py-1.5 text-sm"
                value={clientId} onChange={(e) => void pickClient(e.target.value)}>
                <option value="">Select a client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); void search(); }}>
              <label htmlFor="nta-run-nmi" className="flex-1 text-xs text-gray-600">
                Or find by NMI
                <input id="nta-run-nmi" className="mt-1 block w-full rounded border px-2 py-1.5 font-mono text-sm"
                  value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. 2001003374" />
              </label>
              <Button type="submit" size="sm" variant="outline" disabled={busy || !query.trim()}>
                <Search className="h-4 w-4" /> Find
              </Button>
            </form>
            {matches && matches.length === 0 && <p className="text-sm text-gray-500">No site matches.</p>}
            {matches && matches.length > 0 && (
              <ul className="max-h-40 space-y-1 overflow-auto rounded border p-2 text-sm">
                {matches.map((s) => (
                  <li key={s.id}>
                    <label className="flex cursor-pointer items-center gap-2">
                      <input type="radio" name="nta-site" checked={site?.id === s.id} onChange={() => setSite(s)} />
                      <span className="font-mono">{s.nmi}</span>
                      <span className="truncate text-gray-500">{[s.client?.name, s.addr_city].filter(Boolean).join(' · ')}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <label htmlFor="nta-run-distributor" className="text-xs text-gray-600">
              Distributor
              <select id="nta-run-distributor" className="mt-1 block w-full rounded border px-2 py-1.5 text-sm"
                value={distributor} onChange={(e) => setDistributor(e.target.value)}>
                <option value="">All distributors</option>
                {distributors.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <label htmlFor="nta-run-state" className="text-xs text-gray-600">
              State
              <select id="nta-run-state" className="mt-1 block w-full rounded border px-2 py-1.5 text-sm"
                value={state} onChange={(e) => setState(e.target.value)}>
                {STATES.map((s) => <option key={s} value={s}>{s || 'All states'}</option>)}
              </select>
            </label>
            <label htmlFor="nta-run-portfolio-client" className="col-span-2 text-xs text-gray-600">
              Client
              <select id="nta-run-portfolio-client" className="mt-1 block w-full rounded border px-2 py-1.5 text-sm"
                value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">All clients</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <p className="col-span-2 text-xs text-gray-500">Every enrolled site with a network tariff that matches is queued. Sites with no meter data in the window are marked No data without a full calculation.</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label htmlFor="nta-run-date" className="text-xs text-gray-600">
            Run date <span className="text-gray-400">(projection starts next month)</span>
            <input id="nta-run-date" type="date" className="mt-1 block w-full rounded border px-2 py-1.5 text-sm"
              value={runDate} max={today()} onChange={(e) => setRunDate(e.target.value || today())} />
          </label>
          <label htmlFor="nta-run-label" className="text-xs text-gray-600">
            Label <span className="text-gray-400">(optional)</span>
            <input id="nta-run-label" className="mt-1 block w-full rounded border px-2 py-1.5 text-sm"
              value={label} onChange={(e) => setLabel(e.target.value)} />
          </label>
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => void start()} disabled={!canStart}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />} Start run
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
